import { useEffect, useState } from "react";
import { Home, Share, X, Smartphone, Zap } from "lucide-react";
import { Button } from "@/components/ui/button";

/**
 * InstallPrompt — Invita a "guardar acceso directo" en la pantalla de inicio.
 * Deliberadamente evita palabras como "descargar" o "instalar app" — es un shortcut, no una app store.
 *
 * Modos:
 * - variant="banner" (default): tarjeta discreta en la página
 * - variant="compact": banner delgado dentro del feed
 * - variant="modal": popup centrado (para primera visita post-QR)
 */
export default function InstallPrompt({ variant = "banner", onDismiss }) {
  const [deferred, setDeferred] = useState(null);
  const [visible, setVisible] = useState(false);
  const [isIos, setIsIos] = useState(false);
  const [installed, setInstalled] = useState(false);

  useEffect(() => {
    if (typeof window === "undefined") return;

    const standalone =
      window.matchMedia?.("(display-mode: standalone)").matches ||
      window.navigator.standalone === true;
    if (standalone) {
      setInstalled(true);
      return;
    }

    const dismissed = localStorage.getItem("hr_install_dismissed") === "1";
    if (dismissed && variant !== "modal") return;

    const ua = window.navigator.userAgent || "";
    const iOS = /iPad|iPhone|iPod/.test(ua) && !window.MSStream;
    setIsIos(iOS);

    const onBip = (e) => {
      e.preventDefault();
      setDeferred(e);
      setVisible(true);
    };
    window.addEventListener("beforeinstallprompt", onBip);

    const onInstalled = () => {
      setInstalled(true);
      setVisible(false);
    };
    window.addEventListener("appinstalled", onInstalled);

    // iOS y modal siempre muestran (iOS no dispara `beforeinstallprompt`)
    if (iOS || variant === "modal") setVisible(true);

    return () => {
      window.removeEventListener("beforeinstallprompt", onBip);
      window.removeEventListener("appinstalled", onInstalled);
    };
  }, [variant]);

  const dismiss = () => {
    localStorage.setItem("hr_install_dismissed", "1");
    setVisible(false);
    onDismiss?.();
  };

  const install = async () => {
    if (!deferred) {
      // iOS o navegador sin soporte — cerrar el modal, ya vieron las instrucciones
      if (isIos) dismiss();
      return;
    }
    deferred.prompt();
    const { outcome } = await deferred.userChoice;
    if (outcome === "accepted") setInstalled(true);
    setDeferred(null);
    setVisible(false);
    onDismiss?.();
  };

  if (installed || !visible) return null;

  // ---------- MODAL (popup post-escaneo QR) ----------
  if (variant === "modal") {
    return (
      <div className="fixed inset-0 z-[100] bg-black/70 backdrop-blur-sm grid place-items-center px-5" data-testid="install-modal">
        <div className="relative w-full max-w-md rounded-3xl bg-white shadow-2xl overflow-hidden">
          <button
            onClick={dismiss}
            className="absolute top-3 right-3 w-9 h-9 rounded-full bg-neutral-100 hover:bg-neutral-200 grid place-items-center z-10"
            aria-label="Cerrar"
            data-testid="install-modal-close"
          >
            <X className="w-4 h-4" />
          </button>
          <div className="bg-gradient-to-br from-[#FF007A] to-[#0055FF] px-6 pt-8 pb-6 text-white text-center">
            <div className="w-14 h-14 rounded-2xl bg-white/15 backdrop-blur grid place-items-center mx-auto mb-3">
              <Zap className="w-7 h-7" />
            </div>
            <h2 className="font-heading font-extrabold text-2xl leading-tight">
              Acceso directo a HOLA RADAR
            </h2>
            <p className="text-sm opacity-90 mt-2 leading-relaxed">
              Guárdalo en tu pantalla de inicio y ábrelo con un solo tap.
              <br />
              <strong>Sin descargar apps, sin registros.</strong>
            </p>
          </div>
          <div className="px-6 py-5 space-y-4">
            {isIos ? (
              <ol className="space-y-2.5 text-sm text-neutral-700">
                <IosStep n={1}>Toca el ícono <Share className="inline w-4 h-4 mx-1 -mt-0.5" /> Compartir de Safari</IosStep>
                <IosStep n={2}>Desliza y elige &ldquo;Añadir a pantalla de inicio&rdquo;</IosStep>
                <IosStep n={3}>Confirma con &ldquo;Añadir&rdquo; — listo, ya tienes tu acceso rápido</IosStep>
              </ol>
            ) : (
              <p className="text-sm text-neutral-600 text-center">
                Con un tap se guarda como acceso directo en tu pantalla — igual que un contacto favorito. Se abre al instante, sin actualizaciones, sin permisos raros.
              </p>
            )}
            <div className="grid grid-cols-2 gap-2 pt-1">
              <Button
                onClick={dismiss}
                variant="outline"
                className="rounded-full h-11 font-semibold"
                data-testid="install-modal-later"
              >
                Ahora no
              </Button>
              <Button
                onClick={install}
                className="rounded-full h-11 bg-[#FF007A] hover:bg-[#e5006e] text-white font-bold"
                data-testid="install-modal-add"
              >
                <Home className="w-4 h-4" /> {isIos ? "Entendido" : "Agregar"}
              </Button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // ---------- COMPACT (banner delgado en el feed) ----------
  if (variant === "compact") {
    return (
      <div className="rounded-xl bg-black text-white px-4 py-3 flex items-center gap-3 text-sm" data-testid="install-prompt-compact">
        <Smartphone className="w-4 h-4 shrink-0" />
        <span className="flex-1">
          {isIos ? (
            <>Toca <Share className="inline w-3.5 h-3.5" /> y &ldquo;Añadir a inicio&rdquo; para acceso rápido</>
          ) : (
            <>Guarda un acceso directo en tu inicio — sin descargas</>
          )}
        </span>
        {!isIos && (
          <Button onClick={install} size="sm" className="rounded-full bg-[#FF007A] hover:bg-[#e5006e] h-8 px-3 text-xs font-bold" data-testid="install-prompt-compact-btn">
            Agregar
          </Button>
        )}
        <button onClick={dismiss} data-testid="install-prompt-close" className="opacity-60 hover:opacity-100">
          <X className="w-4 h-4" />
        </button>
      </div>
    );
  }

  // ---------- BANNER (tarjeta blanca) ----------
  return (
    <div className="rounded-2xl border border-neutral-200 bg-white p-4 flex items-start gap-3" data-testid="install-prompt">
      <div className="w-10 h-10 rounded-xl bg-[#FF007A]/10 text-[#FF007A] grid place-items-center shrink-0">
        <Home className="w-5 h-5" />
      </div>
      <div className="flex-1">
        <div className="font-heading font-extrabold">Acceso directo en tu inicio</div>
        <p className="text-sm text-neutral-600 mt-0.5">
          {isIos
            ? "Toca Compartir y selecciona “Añadir a pantalla de inicio” — sin descargas."
            : "Guárdalo en tu pantalla como un contacto favorito. Se abre al instante, sin app store."}
        </p>
        {!isIos && (
          <Button onClick={install} className="mt-3 rounded-full bg-[#FF007A] hover:bg-[#e5006e] font-bold h-9 text-sm" data-testid="install-prompt-btn">
            <Home className="w-4 h-4" /> Agregar acceso directo
          </Button>
        )}
      </div>
      <button onClick={dismiss} className="text-neutral-400 hover:text-neutral-700" data-testid="install-prompt-dismiss">
        <X className="w-4 h-4" />
      </button>
    </div>
  );
}

function IosStep({ n, children }) {
  return (
    <li className="flex items-start gap-3">
      <span className="w-6 h-6 rounded-full bg-[#FF007A] text-white text-xs font-extrabold grid place-items-center shrink-0 mt-0.5">
        {n}
      </span>
      <span>{children}</span>
    </li>
  );
}
